import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle, CardBody } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import {
  AlertTriangle,
  RefreshCw,
  Trash2,
  Eye,
  X,
  RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { FailedEvent } from '../types/admin';

const FailedEvents: React.FC = () => {
  const [page, setPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<FailedEvent | null>(null);
  const pageSize = 20;
  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['failed-events-stats'],
    queryFn: () => adminApiClient.getFailedEventsStats(),
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['failed-events', page, pageSize],
    queryFn: () => adminApiClient.getFailedEvents({ page, page_size: pageSize }),
  });

  const retryMutation = useMutation({
    mutationFn: (eventId: string) => adminApiClient.retryFailedEvent(eventId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Event retried successfully');
      } else {
        toast.error('Retry failed: ' + data.message);
      }
      queryClient.invalidateQueries({ queryKey: ['failed-events'] });
      queryClient.invalidateQueries({ queryKey: ['failed-events-stats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to retry event');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => adminApiClient.deleteFailedEvent(eventId),
    onSuccess: () => {
      toast.success('Event deleted');
      queryClient.invalidateQueries({ queryKey: ['failed-events'] });
      queryClient.invalidateQueries({ queryKey: ['failed-events-stats'] });
      setSelectedEvent(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete event');
    },
  });

  const handleRetry = (event: FailedEvent) => {
    if (window.confirm(`Retry processing event ${event.id.slice(0, 8)}...?`)) {
      retryMutation.mutate(event.id);
    }
  };

  const handleDelete = (event: FailedEvent) => {
    if (window.confirm(`Delete failed event ${event.id.slice(0, 8)}...? This cannot be undone.`)) {
      deleteMutation.mutate(event.id);
    }
  };

  const handleViewDetails = async (event: FailedEvent) => {
    try {
      const fullEvent = await adminApiClient.getFailedEvent(event.id);
      setSelectedEvent(fullEvent);
    } catch (error: any) {
      toast.error('Failed to load event details');
    }
  };

  if (statsLoading || eventsLoading) {
    return <Loading message="Loading failed events..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Failed Events</h1>
        <p className="text-gray-400 mt-1">
          Monitor and manage failed event processing
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Failed"
            value={stats.total.toLocaleString()}
            icon={AlertTriangle}
          />
          <StatCard
            title="Failed Today"
            value={stats.today_count.toLocaleString()}
            subtitle="since 00:00 UTC"
            icon={AlertTriangle}
          />
          <StatCard
            title="Event Types"
            value={Object.keys(stats.by_event_type).length.toString()}
            icon={RefreshCw}
          />
          <StatCard
            title="Oldest Event"
            value={stats.oldest_event ? format(new Date(stats.oldest_event), 'MMM d') : '-'}
            subtitle={stats.oldest_event ? format(new Date(stats.oldest_event), 'HH:mm') : undefined}
          />
          <StatCard
            title="Newest Event"
            value={stats.newest_event ? format(new Date(stats.newest_event), 'MMM d') : '-'}
            subtitle={stats.newest_event ? format(new Date(stats.newest_event), 'HH:mm') : undefined}
          />
        </div>
      )}

      {/* Event Type Breakdown */}
      {stats && Object.keys(stats.by_event_type).length > 0 && (
        <Card>
          <div className="p-6 border-b border-gray-700">
            <CardTitle>By Event Type</CardTitle>
          </div>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_event_type).map(([type, count]) => (
                <Badge key={type} variant="default">
                  {type}: {count}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Failed Events Table */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>Failed Events</CardTitle>
        </div>

        {events && events.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Event ID</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Analysis</TableCell>
                  <TableCell header>Error</TableCell>
                  <TableCell header>Retries</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.data.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <span className="font-mono text-sm text-gray-300">
                        {event.id.slice(0, 8)}...
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="info">{event.event_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-gray-400">
                        {event.analysis_id.slice(0, 8)}...
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-red-400 text-sm truncate max-w-[200px] block">
                        {event.error_message.slice(0, 50)}
                        {event.error_message.length > 50 && '...'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={event.retry_count >= event.max_retries ? 'text-red-400' : 'text-gray-300'}>
                        {event.retry_count}/{event.max_retries}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">
                        {format(new Date(event.created_at), 'MMM d, HH:mm')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(event)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleRetry(event)}
                          disabled={retryMutation.isPending}
                          loading={retryMutation.isPending && retryMutation.variables === event.id}
                          title="Retry"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(event)}
                          disabled={deleteMutation.isPending}
                          loading={deleteMutation.isPending && deleteMutation.variables === event.id}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={events.page}
              totalPages={events.total_pages}
              totalItems={events.total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p>No failed events</p>
          </div>
        )}
      </Card>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <CardTitle>Event Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedEvent(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardBody>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-400 text-sm">Event ID</span>
                    <p className="text-white font-mono text-sm">{selectedEvent.id}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Analysis ID</span>
                    <p className="text-white font-mono text-sm">{selectedEvent.analysis_id}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Event Type</span>
                    <p className="text-white">{selectedEvent.event_type}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Retries</span>
                    <p className="text-white">{selectedEvent.retry_count}/{selectedEvent.max_retries}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Created</span>
                    <p className="text-white">
                      {format(new Date(selectedEvent.created_at), 'MMM d, yyyy HH:mm:ss')}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Last Retry</span>
                    <p className="text-white">
                      {selectedEvent.last_retry_at
                        ? format(new Date(selectedEvent.last_retry_at), 'MMM d, yyyy HH:mm:ss')
                        : '-'}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-gray-400 text-sm">Error Message</span>
                  <p className="text-red-400 mt-1 bg-gray-800 p-3 rounded-md text-sm">
                    {selectedEvent.error_message}
                  </p>
                </div>

                {selectedEvent.payload && (
                  <div>
                    <span className="text-gray-400 text-sm">Payload</span>
                    <pre className="mt-1 bg-gray-800 p-3 rounded-md text-sm text-gray-300 overflow-x-auto">
                      {JSON.stringify(selectedEvent.payload, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="primary"
                    onClick={() => handleRetry(selectedEvent)}
                    disabled={retryMutation.isPending}
                    loading={retryMutation.isPending}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retry Event
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(selectedEvent)}
                    disabled={deleteMutation.isPending}
                    loading={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Event
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FailedEvents;
